import React, { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react';
import { View, Text, Platform, useWindowDimensions, FlatList, RefreshControl, Pressable, ActivityIndicator } from 'react-native';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Animatable from 'react-native-animatable';
import { Entypo, FontAwesome, SimpleLineIcons } from '@expo/vector-icons';
import BottomSheet, { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import AnimatedLottieView from '@utils/lottieAnimation';
import DefaultScreen from '@components/Global/DefaultScreenView';
import AddProjectModal from './Components/AddProject';
import UserInfoModal from './Components/OwnerInfoModal';
import ShareUserModal from '@components/modals/ShareUserModal/ShareUserModal';
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import parseByDelimiter from '@utils/parseByDelimiter';
import RenderProject from './Components/ProjectItem';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import { carouselData } from './Tutorial/CarouselData';
import styles from '@stylesheet';
import filter from 'lodash.filter';
import apis from '@api/api';
import getId from '@api/getId';
import Button from '@components/UI/Button/Button';
import { DefaultRootState } from '@reduxLocal/persistState';
import { ToolbarContext } from '@hooks/toolbarContext';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';

import useGlobalUser from '@hooks/getGlobalUser';
import { XStack } from 'tamagui';
import { debounce } from 'lodash';
import LinkGenModal from '../DataScreen/CaptureScreen/Components/LinkGenModal';
import { useDeviceType } from '@hooks/useDeviceType';
import { HOST } from '@api/constant';
import LottieView from 'lottie-react-native';

const PROJECT_WIDTH = 600;

const CompletedProjects = ({ navigation }) => {
  const queryClient = useQueryClient();
  const refreshTracker = useSelector((state: DefaultRootState) => state.app.refreshTracker);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const [refreshing, setRefreshing] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [columns, setColumns] = useState(0);
  const { width } = useWindowDimensions();
  const [listHorizontal, setListHorizontal] = useState(Platform.OS === 'web' && width > 1600);
  const [query, setQuery] = useState('');
  const bottomDrawerRef = useRef(null);
  const infoModalRef = useRef(null);
  const shareUserRef = useRef(null);
  const [activeProject, setActiveProject] = useState(null);
  const { setProjectContext } = useContext(ToolbarContext);
  const [help, setHelp] = useState(true);
  const robotRef = useRef<RobotHandle>(null);
  const userId = useGlobalUser();
  const [carouselProjectData, setCarouselProjectData] = useState(carouselData);
  const selectedId = useGlobalUser();
  const linkGenRef = useRef(null);
  const { isTablet } = useDeviceType();

  const textHelp = [
    'Find your past completed projects here',
    'You can generate project templates from past projects to use in future projects',
    'Understanding your past projects can help you make better decisions in the future',
    'You can share your projects with other users',
  ]

  const { status, data: projectData, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['completedProjects'],
    queryFn: async () => {
      setCarouselProjectData(carouselData => [{ title: 'Loading Projects', caption: 'Loading your projects', image: <LottieView source={require("@assets/animations/spin-leaf-loader.json")} autoPlay style={{ minHeight: 150, minWidth: 150 }} loop /> }, ...carouselData]);
      const data = await apis.getCompletedProjects();
      setCarouselProjectData(carouselData);
      return data;
    },
    onSuccess: (data) => {
      setFilteredProjects(data);
    },
    refetchOnMount: 'always',
    initialData: queryClient.getQueryData('projectData'),
    staleTime: 1000 * 60 * 15, // 15 minutes
    retry: 2,
  });


  useEffect(() => {
    if (Platform.OS === 'web') {
      setProjectContext({ refreshFn: refetch })
    }
  }, [])

  useEffect(() => {
    const debounceResize = debounce(() => {
      if (width > 1600) {
        const columns = Math.floor(width / PROJECT_WIDTH);
        setColumns(columns);
        setListHorizontal(true);
      } else {
        setListHorizontal(false);
        setColumns(1);
      }
    }, 300);

    debounceResize();
  }, [width]);

  const handleSearch = useCallback((text) => {
    const formattedQuery = text.toLowerCase();
    setQuery(text);
    const filteredData = filter(projectData, project => contains(project, formattedQuery));
    setFilteredProjects(filteredData);
  }, [projectData]);

  const contains = ({ title, description }, query) => {
    return title.toLowerCase().includes(query) || description.toLowerCase().includes(query);
  };

  const changeListView = useCallback(() => {
    if (listHorizontal) {

      const calcColumns = (width) => {
        if (width > 1600) {
          return Math.floor(width / PROJECT_WIDTH);
        } else {
          return 1;
        }
      }

      setColumns(calcColumns(width));
    }
    setListHorizontal(!listHorizontal);
  }, [listHorizontal]);

  const handleModalState = useCallback((item) => {
    setActiveProject(item);
    setDeleteModalVisible(true);
  }, []);

  const _handleShareUser = useCallback((item) => {
    shareUserRef.current?.openModal(item);
  }, [shareUserRef]);

  const _handleSelectUser = useCallback((item) => {
    infoModalRef.current?.toggleVisibility(item);
  }, []);

  const handleShowHelp = () => {
    // setHelp(true);
    robotRef.current!.showHelp();
  }

  const MemoizedProjectData = React.memo(RenderProject);

  const qrCodeCallback = (item) => {
    linkGenRef.current?.openModal(item);
  };

  const _renderItem = useCallback(({ item, index }) => {
    return (
      <MemoizedProjectData
        navigation={navigation}
        qrCodeCallback={qrCodeCallback}
        item={item}
        complete
        index={index}
        horizontal={listHorizontal}
        _handleModalState={handleModalState}
        _handleSelectUser={_handleSelectUser}
        _handleShareUser={_handleShareUser}
      />
    )
  }, [filteredProjects]);

  return (
    <View style={{ flex: 1 }}>
      <LinkGenModal caption='You can easily share your projects using the QR code' heading='Share your project' url={__DEV__ ? `https://${HOST}:4000/projects/` : "https://api.smartsaas.pro/projects/"} ref={linkGenRef} />
      <DefaultScreen
        backButton
        style={{ flex: 1, paddingLeft: 0, paddingTop: 10, paddingBottom: 70 }}
        scrollDisabled
        searchBar={{
          callback: handleSearch,
          key: 'projectSearch',
          placeHolder: 'Search past projects',
          customIcons: (Platform.OS === 'web' || isTablet) ? [{
            icon: listHorizontal ? <FontAwesome name="th-list" size={18} color={"white"} style={{ pointerEvents: "none" }} /> : <SimpleLineIcons name="grid" size={18} color="white" style={{ pointerEvents: "none" }} />,
            function: changeListView,
            functionName: 'Change View',
          }, {
            icon: <Entypo name="help" size={18} color={"white"} />,
            function: handleShowHelp,
            functionName: 'Show Help',
          },
          {
            icon: <FontAwesome name="plus-circle" size={18} color="white" />,
            function: () => { Platform.OS === 'web' ? bottomDrawerRef.current?.openModal() : bottomDrawerRef.current?.present() },
            functionName: 'Add Project',
          }
          ] : [
            {
              icon: <FontAwesome name="plus-circle" size={18} color="white" />,
              function: () => { bottomDrawerRef.current?.present() },
              functionName: 'Add Project',
            },
            {
              icon: <Entypo name="help" size={18} color={"white"} />,
              function: handleShowHelp,
              functionName: 'Show Help',
            }
          ]
        }}
      >
        <UserInfoModal ref={infoModalRef} />
        <ShareUserModal ref={shareUserRef} title={activeProject?.title} shareFunction={apis.shareProject} />
        <DeleteConfirmModal deleteFunction={apis.deleteProject} itemS={activeProject} title={parseByDelimiter(activeProject?.name || "")} refreshFunction={refetch} setState={setDeleteModalVisible} visibleState={deleteModalVisible} />
        <View style={{ flexDirection: 'column-reverse', flex: 1, alignSelf: "stretch" }}>
          <FlatList
            key={listHorizontal ? 'horizontal' + columns : 'vertical' + columns}
            data={filteredProjects.reverse()}
            horizontal={listHorizontal}
            nestedScrollEnabled
            maxToRenderPerBatch={4}
            windowSize={6}
            centerContent
            onRefresh={refetch}
            refreshing={isFetching || isLoading}
            directionalLockEnabled
            contentContainerStyle={{ paddingBottom: listHorizontal ? 0 : 150, maxHeight: listHorizontal ? "100%" : undefined }}
            numColumns={listHorizontal ? undefined : columns}
            showsVerticalScrollIndicator={false}
            indicatorStyle='black'
            renderItem={_renderItem}
            ListEmptyComponent={(
              <View style={{ flex: 1, alignSelf: 'center', alignContent: 'center', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {query != '' ?
                  <View style={[styles.noteContainer, { flex: 1, margin: 50, padding: 5, paddingBottom: 20 }]}>
                    <AnimatedLottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ height: 100, width: 100, flex: 1 }} />
                    <Text style={{ flex: 1 }}>No items found matching query</Text>
                  </View>
                  :
                  <View style={{ gap: 30, margin: 30 }}>
                    <View style={[styles.centerEverything, { gap: 30, flex: 1, maxWidth: "100%", alignSelf: "stretch" }]}>
                      {
                        isFetching || isLoading &&
                        <ActivityIndicator size="large" color="white" />
                      }
                      <GlobalTutorial customHeight={350} customWidth={300} carouselData={carouselProjectData} />
                    </View>
                  </View>
                }
              </View>
            )}
            ListFooterComponentStyle={{ alignSelf: 'center', maxWidth: '100%', justifyContent: 'center', paddingBottom: 50 }}
            style={{ flex: 3, alignSelf: 'stretch', }}
            refreshControl={listHorizontal ? null : <RefreshControl refreshing={refreshing} onRefresh={refetch} />}
            scrollEnabled={true}
            keyExtractor={item => item._id}
          />
        </View>
      </DefaultScreen>
      <AddProjectModal ref={bottomDrawerRef} notContext callBack={refetch} />
      <View style={{ position: "absolute", bottom: isTablet ? 0 : 100, left: isTablet && 150 }}>
        <SwipeableRobot ref={robotRef} textHelp={textHelp} />
      </View>
    </View >
  )
};

export default CompletedProjects;
